import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '../../services/toastService'
import { adminAPI, vendorAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { trackAdminContactUpdate } from '../../utils/analytics'
import AdminHeader from '../../components/admin/AdminHeader'
import ContactSubmissionDetails from '../../components/admin/ContactSubmissionDetails'
import ContactAdminActions from '../../components/admin/ContactAdminActions'
import { 
  ArrowLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ContactDetail')

/**
 * ContactDetail - Admin contact submission management page
 * Orchestrates contact data display and status updates
 * Optimized from 294 lines by extracting display and action components
 */
export default function ContactDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading, error, execute } = useAsyncOperation()
  const [contact, setContact] = useState(null)
  const [notes, setNotes] = useState('')
  const [inviteSent, setInviteSent] = useState(false)

  useEffect(() => {
    loadContact()
  }, [id])

  const loadContact = async () => {
    await execute(
      async () => {
        const response = await adminAPI.getContact(id)
        const contactData = response.data
        setContact(contactData)
        setNotes(contactData?.notes || '')
        return contactData
      },
      { errorMessage: 'Failed to load contact details' }
    )
  }

  const sendVendorInvite = async () => {
    try {
      await vendorAPI.sendInvite({
        email: contact.email,
        track: 'vendor',
        entityId: contact.entityId || undefined,
        message: `Your vendor request for ${contact.entityName || 'your business'} has been approved.`
      })
      setInviteSent(true)
      toast.success('Vendor invite sent')
    } catch (err) {
      logger.error('Failed to send vendor invite', err)
      toast.error('Failed to send invite')
    }
  }

  const updateStatus = async (newStatus) => {    const oldStatus = contact?.status
    try {
      await adminAPI.updateContactStatus(id, { status: newStatus, notes })
      
      trackAdminContactUpdate(id, newStatus, oldStatus)
      
      toast.success(t('toast.success.statusUpdated'))
      loadContact()
    } catch (error) {
      logger.error('Failed to update status', error)
      toast.error(t('toast.error.statusUpdateFailed'))
    }
  }

  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        color: 'bg-yellow-100 text-yellow-800', 
        icon: ClockIcon,
        label: 'Pending'
      },
      reviewed: { 
        color: 'bg-blue-100 text-blue-800', 
        icon: CheckCircleIcon,
        label: 'Reviewed'
      },
      resolved: { 
        color: 'bg-green-100 text-green-800', 
        icon: CheckCircleIcon,
        label: 'Resolved'
      },
      spam: { 
        color: 'bg-red-100 text-red-800', 
        icon: XCircleIcon,
        label: 'Spam'
      }
    }
    return configs[status] || configs.pending
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('app.loading')}</div>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Contact submission not found</p>
          <button
            onClick={() => navigate('/admin/contacts')}
            className="text-primary hover:text-primary-600"
          >
            {t('admin.backToCollection')}
          </button>
        </div>
      </div>
    )
  }

  const statusConfig = getStatusConfig(contact.status)
  const StatusIcon = statusConfig.icon

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin/contacts')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('admin.backToCollection')}
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Contact Submission</h1>
              <p className="text-gray-500 mt-1">Manage contact inquiry</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                <StatusIcon className="h-4 w-4 mr-1" />
                {statusConfig.label}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Info Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {contact.subject || '(No subject)'}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>Submitted {new Date(contact.createdAt).toLocaleString()}</span>
                {contact.isVendor && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <TruckIcon className="h-3 w-3 mr-1" />
                    Vendor Request
                  </span>
                )}
              </div>
            </div>

            <ContactSubmissionDetails contact={contact} />
          </div>
        </div>

        {/* Admin Actions */}
        <ContactAdminActions
          notes={notes}
          onNotesChange={setNotes}
          currentStatus={contact.status}
          onStatusUpdate={updateStatus}
          loading={loading}
        />

        {/* Send Vendor Invite — only for vendor requests */}
        {contact.isVendor && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <TruckIcon className="h-4 w-4 text-purple-600" />
              Vendor Invite
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Send a vendor onboarding invite to <strong>{contact.email}</strong>
              {contact.entityName && <> for <strong>{contact.entityName}</strong></>}.
            </p>
            <button
              type="button"
              onClick={sendVendorInvite}
              disabled={inviteSent}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {inviteSent ? 'Invite Sent' : 'Send Vendor Invite'}
            </button>
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Submission ID</div>
            <div className="mt-1 font-mono text-xs text-gray-900 break-all">{contact.id}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Created</div>
            <div className="mt-1 text-sm text-gray-900">
              {new Date(contact.createdAt).toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Updated</div>
            <div className="mt-1 text-sm text-gray-900">
              {new Date(contact.updatedAt).toLocaleString()}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  )
}
